import React from 'react';
import { List, ListItem, ListItemIcon, ListItemText, Typography, Box, Divider } from '@mui/material';
import { RecentActivity } from '../../../services/dashboardService';
import { useTranslation } from 'react-i18next';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import BuildIcon from '@mui/icons-material/Build';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

interface RecentActivityListProps {
  activities: RecentActivity[];
}

/**
 * Composant pour afficher la liste des activités récentes
 */
const RecentActivityList: React.FC<RecentActivityListProps> = ({ activities }) => {
  const { t, i18n } = useTranslation();

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'fuel':
        return <LocalGasStationIcon sx={{ color: '#f57c00' }} />;
      case 'maintenance':
        return <BuildIcon sx={{ color: '#1976d2' }} />;
      case 'member':
        return <PersonAddIcon sx={{ color: '#4caf50' }} />;
      case 'vehicle':
        return <DirectionsCarIcon sx={{ color: '#9c27b0' }} />;
      default:
        return null;
    }
  };

  const formatTimeAgo = (date: Date) => {
    const locale = i18n.language === 'fr' ? fr : enUS;
    return formatDistanceToNow(date, { addSuffix: true, locale });
  };

  return (
    <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
      {activities.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard:errors.noData')}
          </Typography>
        </Box>
      ) : (
        activities.map((activity, index) => (
          <React.Fragment key={activity.id}>
            <ListItem alignItems="flex-start">
              <ListItemIcon>{getActivityIcon(activity.type)}</ListItemIcon>
              <ListItemText
                primary={activity.description}
                secondary={
                  <Typography
                    sx={{ display: 'inline' }}
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {formatTimeAgo(activity.timestamp)}
                  </Typography>
                }
              />
            </ListItem>
            {index < activities.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))
      )}
    </List>
  );
};

export default RecentActivityList;